import React, { useState } from 'react';
import { Shield, CreditCard, Loader2, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const GovLoginIntegration = ({ onGovLoginSuccess, onFallbackToLocal }) => {
  const [loading, setLoading] = useState(false);
  const [cacStatus, setCacStatus] = useState('checking'); // checking, available, unavailable
  const [govLoginStep, setGovLoginStep] = useState('init'); // init, redirecting, processing, success, error

  // Add Google login handler
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  // Simulate CAC card detection
  React.useEffect(() => {
    const checkCACCard = async () => {
      // Simulate CAC card detection
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, randomly show CAC as available or not
      const hasCAC = Math.random() > 0.5;
      setCacStatus(hasCAC ? 'available' : 'unavailable');
    };

    checkCACCard();
  }, []);

  const handleGovernmentLogin = async () => {
    setLoading(true);
    setGovLoginStep('redirecting');

    try {
      // Redirect to the actual DMDC authentication system
      const dmcAuthUrl = 'https://myauth.dmdc.mil/app/myauth-prod-dmdc-mil_milconnect_1/exkg5lx9aX5cAampe0k6/sso/saml';
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      setGovLoginStep('processing');

      // In a production environment, this would:
      // 1. Redirect to DMDC authentication
      // 2. Handle SAML response
      // 3. Extract military data from DMDC
      // 4. Create/update user profile with live data
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show the redirect notice for real DMDC integration
      setGovLoginStep('redirect-notice');
      
    } catch (error) {
      setGovLoginStep('error');
      setLoading(false);
    }
  };

  const renderCACStatus = () => {
    if (cacStatus === 'checking') {
      return (
        <div className="flex items-center gap-2 text-yellow-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Detecting CAC card...</span>
        </div>
      );
    }

    if (cacStatus === 'available') {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">CAC card detected</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 text-orange-600">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">No CAC card detected</span>
      </div>
    );
  };

  if (govLoginStep === 'redirecting') {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ExternalLink className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Redirecting to DoD Portal
        </h3>
        <p className="text-gray-600 mb-4">
          You're being redirected to the official Department of Defense login...
        </p>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            🔒 Secure connection to milconnect.dmdc.osd.mil
          </p>
        </div>
      </div>
    );
  }

  if (govLoginStep === 'processing') {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Authenticating with DoD
        </h3>
        <p className="text-gray-600 mb-4">
          Verifying your military credentials and importing your profile...
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>CAC authentication verified</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Military records accessed</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Importing benefits data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (govLoginStep === 'redirect-notice') {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ExternalLink className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Ready to Connect to DMDC Portal
        </h3>
        <p className="text-gray-600 mb-4">
          To complete government authentication, you'll be redirected to the official DMDC authentication system.
        </p>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
          <p className="text-sm text-blue-800 mb-2">
            🔒 Official DMDC Integration Process:
          </p>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Authenticates with your CAC card or DS Logon credentials</li>
            <li>• Automatically imports your live military data from DEERS</li>
            <li>• Syncs current benefits, assignments, and family information</li>
            <li>• Updates your MilConnect AI profile with official records</li>
            <li>• Enables real-time benefit status and eligibility checking</li>
          </ul>
        </div>
        <div className="space-y-3">
          <Button
            onClick={() => {
              // In production, this would redirect to the actual DMDC URL
              window.open('https://myauth.dmdc.mil/app/myauth-prod-dmdc-mil_milconnect_1/exkg5lx9aX5cAampe0k6/sso/saml', '_blank');
            }}
            className="bg-blue-600 hover:bg-blue-700 w-full"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Continue to DMDC Portal
          </Button>
          <Button
            onClick={onFallbackToLocal}
            variant="outline"
            className="w-full"
          >
            Use Local Login Instead
          </Button>
        </div>
        <div className="mt-4 text-xs text-gray-500">
          <p>Note: Government authentication requires proper DoD credentials and system access.</p>
        </div>
      </div>
    );
  }

  if (govLoginStep === 'success') {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Government Authentication Successful
        </h3>
        <p className="text-gray-600 mb-4">
          Your official military profile has been imported and enhanced with AI features
        </p>
        <Badge className="bg-green-100 text-green-800">
          Authenticated via DoD Portal
        </Badge>
      </div>
    );
  }

  if (govLoginStep === 'error') {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Government Login Failed
        </h3>
        <p className="text-gray-600 mb-4">
          Unable to authenticate with the DoD portal. This could be due to network issues or system maintenance.
        </p>
        <div className="space-y-3">
          <Button
            onClick={() => {
              setGovLoginStep('init');
              setLoading(false);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Try Again
          </Button>
          <Button
            onClick={onFallbackToLocal}
            variant="outline"
          >
            Use Local Login Instead
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Government Login Option */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl text-blue-900">
            Official Government Login
          </CardTitle>
          <p className="text-blue-700">
            Connect to myauth.dmdc.mil for official military records
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="font-semibold text-gray-900 mb-2">Authentication Methods:</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">CAC Card</span>
                </div>
                {renderCACStatus()}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-sm">DS Logon</span>
                </div>
                <Badge variant="outline" className="text-xs">Available</Badge>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-900">Official DMDC Integration:</p>
                <ul className="text-yellow-800 mt-1 space-y-1">
                  <li>• Real-time access to official military records</li>
                  <li>• Direct connection to myauth.dmdc.mil portal</li>
                  <li>• CAC card and DS Logon authentication</li>
                  <li>• Automatic synchronization with DEERS database</li>
                </ul>
              </div>
            </div>
          </div>

          <Button
            onClick={handleGovernmentLogin}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-3"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Connecting to DoD Portal...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Login with Government Account
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Google Login Option */}
      <Card className="border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <CardContent className="pt-6">
          <Button
            onClick={handleGoogleLogin}
            disabled={loading}
            variant="outline"
            className="w-full py-3 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">or continue with</span>
        </div>
      </div>

      {/* Local Login Fallback */}
      <Button
        onClick={onFallbackToLocal}
        variant="outline"
        className="w-full py-3"
        disabled={loading}
      >
        Use Local MilConnect AI Account
      </Button>

      {/* Help Text */}
      <div className="text-center">
        <p className="text-xs text-gray-500">
          Government login provides real-time access to your official military records.
          <br />
          Local accounts offer enhanced AI features with manual data import.
        </p>
      </div>
    </div>
  );
};

export default GovLoginIntegration;